/*
 * <newsletter-signup> — progressive enhancement for the Newsletter Signup section.
 *
 * Without JS the inner <form> posts to Klaviyo's hosted subscribe page (action attr).
 * With JS we intercept and POST to Klaviyo's Create Client Subscription endpoint, which:
 *   - creates/updates the profile immediately
 *   - subscribes it to the configured List ID (honouring the list's double opt-in setting)
 *   - triggers any list-triggered flow (e.g. Welcome Flow)
 *   - preserves Klaviyo analytics (uses the public Site ID)
 * Docs: https://developers.klaviyo.com/en/reference/create_client_subscription
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var REVISION = '2025-01-15';
  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  class NewsletterSignup extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="email"]');
      this.button = this.querySelector('button[type="submit"]');
      this.message = this.querySelector('.newsletter-form__message');

      this.listId = this.dataset.listId;
      this.publicKey = this.dataset.publicKey;
      this.source = this.dataset.source || '';
      this.submitting = false;

      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }
    }

    onSubmit(event) {
      // Without a configured key/list, let the native hosted form submit (fallback).
      if (!this.publicKey || !this.listId) return;

      event.preventDefault();
      if (this.submitting) return;

      var email = (this.input.value || '').trim();
      if (!EMAIL_RE.test(email)) {
        this.input.focus();
        this.setMessage(this.dataset.error, true);
        return;
      }

      this.setBusy(true);
      this.clearMessage();

      fetch(ENDPOINT + '?company_id=' + encodeURIComponent(this.publicKey), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: REVISION
        },
        body: JSON.stringify(this.buildPayload(email))
      })
        .then(function (response) {
          // 2xx (typically 202 Accepted) = success.
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response;
        })
        .then(this.onSuccess.bind(this))
        .catch(this.onError.bind(this));
    }

    buildPayload(email) {
      var attributes = { email: email };
      if (this.source) {
        attributes.properties = { source: this.source };
      }

      var subscription = {
        type: 'subscription',
        attributes: {
          profile: {
            data: { type: 'profile', attributes: attributes }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: this.listId } }
        }
      };

      if (this.source) {
        subscription.attributes.custom_source = this.source;
      }

      return { data: subscription };
    }

    onSuccess() {
      this.setBusy(false);
      this.form.reset();
      this.setMessage(this.dataset.success, false);
    }

    onError() {
      this.setBusy(false);
      this.setMessage(this.dataset.error, true);
    }

    setBusy(state) {
      this.submitting = state;
      this.form.setAttribute('aria-busy', state ? 'true' : 'false');
      if (this.button) this.button.disabled = state;
    }

    setMessage(text, isError) {
      if (!this.message) return;
      this.classList.toggle('newsletter-form--error', !!isError);
      this.message.textContent = text || '';
      this.message.hidden = !text;
    }

    clearMessage() {
      if (!this.message) return;
      this.message.hidden = true;
      this.message.textContent = '';
      this.classList.remove('newsletter-form--error');
    }
  }

  if (!customElements.get('newsletter-signup')) {
    customElements.define('newsletter-signup', NewsletterSignup);
  }
})();
